import React from "react";
import { useParams, Link } from "react-router-dom";
import { Github } from "lucide-react";
import { ExportArrowUpRight } from "@vectopus/atlas-icons-react";
import { Page, BackLink, Tag } from "./ui";
import { getProjectBySlug } from "../data/portfolio";

export default function SoloProject() {
  const { name } = useParams();
  const project = getProjectBySlug(name);

  if (!project) {
    return (
      <Page className="gap-6">
        <BackLink />
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">
            Project not found
          </h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            The project you’re looking for doesn’t exist.{" "}
            <Link
              to="/projects"
              className="text-neutral-900 underline underline-offset-4 dark:text-white"
            >
              Browse all projects
            </Link>
            .
          </p>
        </div>
      </Page>
    );
  }

  return (
    <Page className="gap-8">
      <BackLink />

      <article className="space-y-6">
        <header className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white sm:text-3xl">
              {project.name}
            </h1>
            <div className="flex items-center gap-4 text-neutral-600 dark:text-neutral-400">
              <a
                href={project.live}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${project.name} live site in a new tab`}
                className="rounded-md transition-colors duration-200 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30 dark:hover:text-white dark:focus-visible:ring-white/40"
              >
                <ExportArrowUpRight size={20} />
              </a>
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`View ${project.name} source on GitHub in a new tab`}
                className="rounded-md transition-colors duration-200 hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/30 dark:hover:text-white dark:focus-visible:ring-white/40"
              >
                <Github size={20} />
              </a>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {project.tech.map((tech) => (
              <Tag key={tech}>{tech}</Tag>
            ))}
          </div>
        </header>

        <img
          src={project.image}
          alt={`${project.name} preview`}
          loading="lazy"
          className="w-full border grayscale border-black/10 object-contain dark:border-white/10"
        />

        <p className="text-sm leading-relaxed text-neutral-600 dark:text-neutral-400 sm:text-base">
          {project.description}
        </p>
      </article>
    </Page>
  );
}
